import { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import Modal from "react-bootstrap/Modal";
import Navbar from "../components/navbar/Navbar";
import { getAllHelpContent } from "../helpers/axios/helpAxios";
import { agGridTheme } from "../helpers/constants/Constants";

const SECTION_LINKS = [
  { id: "training-help", label: "HELP" },
  { id: "training-videos", label: "TRAINING VIDEOS" },
  { id: "training-workflow-documents", label: "WORKFLOW DOCUMENTS" },
  { id: "training-super-users", label: "KEY CONTACTS" },
  { id: "training-feedback", label: "FEEDBACK" },
];

const SUPER_USERS = [
  { name: "David Bennett", region: "Central", role: "Engineering Digital Lead" },
  { name: "Lee Woods", region: "Central", role: "TSG Technical Manager" },
  { name: "Matthew Jeckles", region: "Central", role: "CAD Manager" },
  { name: "Andrew Radley", region: "UU", role: "RDDL" },
  { name: "David Macculloch", region: "Central/ESD", role: "CAD" },
  { name: "Brandyn Bain", region: "ESD", role: "RDDL" },
  { name: "Mark j Wilson", region: "Central/ST", role: "CAD" },
  { name: "Sawsan Saadeh", region: "ST/SWW/NIW", role: "RDDL" },
];

const WORKFLOW_DOCUMENTS_MANIFEST_URL =
  `${process.env.PUBLIC_URL}/workflowDocuments/workflowDocuments.json`;

const TICKET_URL =
  "https://ricohemea.service-now.com/login_with_sso.do?glide_sso_id=8a4afffc9771e9103aad31c3f153af94";

const Help = () => {
  const [videoData, setVideoData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [internalSelected, setInternalSelected] = useState("NA");
  const [workflowDocuments, setWorkflowDocuments] = useState([]);
  const [filteredWorkflowDocCount, setFilteredWorkflowDocCount] = useState(0);
  const [workflowDocViewMode, setWorkflowDocViewMode] = useState("table");
  const [workflowDocCardSearchTerm, setWorkflowDocCardSearchTerm] = useState("");
  const [workflowDocCardRegionFilter, setWorkflowDocCardRegionFilter] =
    useState("All");
  const [workflowDocCardCategoryFilter, setWorkflowDocCardCategoryFilter] =
    useState("All");
  const [selectedWorkflowDoc, setSelectedWorkflowDoc] = useState(null);
  const [showWorkflowDocModal, setShowWorkflowDocModal] = useState(false);

  useEffect(() => {
    fetchAllHelpContent();
  }, []);

  useEffect(() => {
    const loadWorkflowDocuments = async () => {
      try {
        const response = await fetch(WORKFLOW_DOCUMENTS_MANIFEST_URL, {
          cache: "no-store",
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!Array.isArray(data) || data.length === 0) return;

        const normalizedDocs = data
          .map((doc, index) => ({
            id: Number(doc?.id ?? index + 1),
            title: String(doc?.title ?? "Untitled Document"),
            region: String(doc?.region ?? "All"),
            category: String(doc?.category ?? "General"),
            fileName: String(doc?.fileName ?? ""),
          }))
          .filter((doc) => doc.fileName.trim().length > 0);

        if (normalizedDocs.length > 0) setWorkflowDocuments(normalizedDocs);
      } catch {
        // Keep empty state if manifest cannot be loaded.
      }
    };

    loadWorkflowDocuments();
  }, []);

  useEffect(() => {
    if (!workflowDocuments.length) {
      setSelectedWorkflowDoc(null);
      return;
    }

    const selectedExists = workflowDocuments.some(
      (doc) => doc.id === selectedWorkflowDoc?.id,
    );

    if (!selectedExists) setSelectedWorkflowDoc(null);
  }, [workflowDocuments, selectedWorkflowDoc]);

  const fetchAllHelpContent = async () => {
    const data = await getAllHelpContent();
    const orderedData = Array.isArray(data)
      ? [...data].sort((a, b) => (a?.title ?? "").localeCompare(b?.title ?? ""))
      : [];
    setVideoData(orderedData);
  };

  const feedbackSrc =
    internalSelected === "yes"
      ? "https://forms.office.com/Pages/ResponsePage.aspx?id=u0P4N89WbUqYuS6sNuu2BpNFPfgTxNREp_qui--RjWtUMjBaQUQ1NkxYUkE2ODZCSlpOTEtYUlRUSC4u&embed=true"
      : internalSelected === "no"
        ? "https://forms.office.com/e/hUcVjJ8sgx"
        : null;

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filteredVideos = videoData?.filter((video) => {
    const searchable = `${video?.title ?? ""} ${video?.tags ?? ""}`;
    return searchable.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const superUserColumnDefs = useMemo(
    () => [
      {
        field: "region",
        headerName: "Region",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "name",
        headerName: "User",
        flex: 1.2,
        minWidth: 220,
        sort: "asc",
      },
      {
        field: "role",
        headerName: "Role",
        flex: 1.2,
        minWidth: 220,
      },
    ],
    [],
  );

  const workflowDocColumnDefs = useMemo(
    () => [
      {
        field: "title",
        headerName: "Document",
        flex: 1.4,
        minWidth: 240,
      },
      {
        field: "region",
        headerName: "Region",
        flex: 0.8,
        minWidth: 140,
      },
      {
        field: "category",
        headerName: "Category",
        flex: 0.8,
        minWidth: 140,
      },
    ],
    [],
  );

  const workflowDocDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agSetColumnFilter",
      resizable: true,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  const getWorkflowDocUrl = (fileName) => {
    const safeFilePath = String(fileName)
      .split("/")
      .map((segment) => encodeURIComponent(segment))
      .join("/");
    return `${process.env.PUBLIC_URL}/workflowDocuments/${safeFilePath}`;
  };

  const getFileExtension = (fileName) => {
    const parts = String(fileName ?? "").toLowerCase().split(".");
    return parts.length > 1 ? parts.pop() : "";
  };

  const selectedWorkflowDocExtension = getFileExtension(
    selectedWorkflowDoc?.fileName,
  );

  const isInlinePreviewable = [
    "pdf",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "svg",
    "txt",
  ].includes(selectedWorkflowDocExtension);

  const isOfficeFile = [
    "doc",
    "docx",
    "xls",
    "xlsx",
    "ppt",
    "pptx",
  ].includes(selectedWorkflowDocExtension);

  const getWorkflowDocAbsoluteUrl = (fileName) => {
    const relativePath = getWorkflowDocUrl(fileName);
    return new URL(relativePath, window.location.origin).href;
  };

  const getWorkflowDocPreviewUrl = (doc) => {
    if (!doc?.fileName) return null;
    if (isInlinePreviewable) return getWorkflowDocUrl(doc.fileName);
    if (isOfficeFile) {
      const absoluteUrl = getWorkflowDocAbsoluteUrl(doc.fileName);
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(absoluteUrl)}`;
    }
    return null;
  };

  const selectedWorkflowDocUrl = selectedWorkflowDoc
    ? getWorkflowDocUrl(selectedWorkflowDoc.fileName)
    : null;

  const selectedWorkflowDocPreviewUrl = selectedWorkflowDoc
    ? getWorkflowDocPreviewUrl(selectedWorkflowDoc)
    : null;

  const superUserDefaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agSetColumnFilter",
      resizable: true,
      suppressHeaderMenuButton: false,
    }),
    [],
  );

  const updateWorkflowDocCount = (api) => {
    if (!api) return;
    setFilteredWorkflowDocCount(api.getDisplayedRowCount());
  };

  const workflowDocCardRegions = useMemo(
    () => [
      "All",
      ...new Set(
        workflowDocuments
          .map((doc) => String(doc?.region ?? "").trim())
          .filter(Boolean),
      ),
    ],
    [workflowDocuments],
  );

  const workflowDocCardCategories = useMemo(
    () => [
      "All",
      ...new Set(
        workflowDocuments
          .map((doc) => String(doc?.category ?? "").trim())
          .filter(Boolean),
      ),
    ],
    [workflowDocuments],
  );

  const filteredWorkflowDocCards = workflowDocuments.filter((doc) => {
    const searchable = `${doc?.title ?? ""} ${doc?.region ?? ""} ${doc?.category ?? ""}`;
    const matchesSearch = searchable
      .toLowerCase()
      .includes(workflowDocCardSearchTerm.toLowerCase());

    const matchesRegion =
      workflowDocCardRegionFilter === "All" ||
      doc?.region === workflowDocCardRegionFilter;

    const matchesCategory =
      workflowDocCardCategoryFilter === "All" ||
      doc?.category === workflowDocCardCategoryFilter;

    return matchesSearch && matchesRegion && matchesCategory;
  });

  const openWorkflowDocument = (doc) => {
    if (!doc) return;
    setSelectedWorkflowDoc(doc);
    setShowWorkflowDocModal(true);
  };

  return (
    <div className="training-page home-page">
      <Navbar />
      <main className="home-main training-main">
        <section className="training-page-layout" aria-label="Training sections">
          <aside className="training-side-nav">
            <p className="training-side-nav__title">Jump To</p>
            <nav aria-label="Training section navigation">
              {SECTION_LINKS.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className="training-side-nav__link"
                  onClick={() => scrollToSection(section.id)}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          <div className="training-content-column">
            <section className="home-hero training-hero">
              <div className="home-hero__aurora home-hero__aurora--one" />
              <div className="home-hero__aurora home-hero__aurora--two" />
              <div className="home-hero__grid" />

              <div className="home-hero__content training-hero__content">
                <p className="home-kicker">Help Hub</p>
                <h1>Help and Support</h1>
                <p className="home-subcopy">
                  Use the fast navigation panel to the left to jump to the relevant section you need to help you, learn, raise a ticket or provide feedback.
                </p>
              </div>

              <aside className="home-hero__panel training-hero__panel">
                <h2>What You Can Do Here</h2>
                <ul>
                  <li>Understand how to get help, raise a ticket</li>
                  <li>Search and watch training videos</li>
                  <li>Access workflow and support resources quickly</li>
                  <li>Find your local regional champion for help</li>
                  <li>Submit targeted feedback to the right people</li>
                </ul>
              </aside>
            </section>

            <div className="training-section-stack">
              <section id="training-help" className="home-section training-hub-section">
              <div className="home-section__header">
                <p className="home-kicker">HELP</p>
                <h2>Get support quickly</h2>
              </div>

              <div className="home-pillars__grid">
                <button
                  type="button"
                  className="home-card training-help-card-link"
                  onClick={() => scrollToSection("training-videos")}
                >
                  <h3>Start Here</h3>
                  <p>
                    New to mView? Begin with the training videos section below,
                    then move to workflow documents for step-by-step guidance.
                  </p>
                </button>
                <button
                  type="button"
                  className="home-card training-help-card-link"
                  onClick={() => scrollToSection("training-super-users")}
                >
                  <h3>Need Assistance</h3>
                  <p>
                    If your workflow is blocked, contact a key contact
                    or site lead for priority support.
                  </p>
                </button>
                <article className="home-card training-help-report-card">
                  <h3>Report an Issue</h3>
                <a
                  href={TICKET_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-btn home-btn--solid training-help-report-card__btn"
                >
                  Raise a Ticket
                </a>
                </article>
              </div>
            </section>

            <section id="training-videos" className="home-section training-hub-section">
              <div className="home-section__header">
                <p className="home-kicker">TRAINING VIDEOS</p>
                <h2>
                  {filteredVideos.length > 0
                    ? `${filteredVideos.length} video${filteredVideos.length === 1 ? "" : "s"} available`
                    : "No videos found"}
                </h2>
              </div>

              <div className="training-search-wrap">
                <label className="training-search-label" htmlFor="training-search">
                  Search Videos
                </label>
                <input
                  id="training-search"
                  type="text"
                  placeholder="Search by title or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="training-search-input"
                />
              </div>

              {filteredVideos.length > 0 ? (
                <div className="training-video-grid">
                  {filteredVideos.map((video) => (
                    <article key={video.id} className="training-video-card">
                      <div className="training-video-frame">
                        <video
                          controls
                          poster={`${process.env.PUBLIC_URL}/trainingContent/images/${video.imageFileName}`}
                        >
                          <source
                            src={`${process.env.PUBLIC_URL}/trainingContent/videos/${video.videoFileName}`}
                            type="video/mp4"
                          />
                          Your browser does not support the video tag.
                        </video>
                      </div>

                      <div className="training-video-meta">
                        {video?.title && <h3>{video.title}</h3>}
                        {/* {video?.tags && <p>{video.tags}</p>} */}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="training-empty-state">
                  No videos match your search yet. Try a broader keyword.
                </div>
              )}
            </section>

            <section
              id="training-workflow-documents"
              className="home-section training-hub-section"
            >
              <div className="home-section__header">
                <p className="home-kicker">WORKFLOW DOCUMENTS</p>
                <h2>
                  {workflowDocViewMode === "cards"
                    ? filteredWorkflowDocCards.length
                    : filteredWorkflowDocCount}
                  {" "}
                  document
                  {(workflowDocViewMode === "cards"
                    ? filteredWorkflowDocCards.length
                    : filteredWorkflowDocCount) === 1
                    ? ""
                    : "s"}
                </h2>
              </div>

              <div className="training-workflow-view-toggle">
                <button
                  type="button"
                  className={`home-btn ${workflowDocViewMode === "table" ? "home-btn--solid" : "home-btn--ghost"}`}
                  onClick={() => setWorkflowDocViewMode("table")}
                >
                  Table only
                </button>
                <button
                  type="button"
                  className={`home-btn ${workflowDocViewMode === "cards" ? "home-btn--solid" : "home-btn--ghost"}`}
                  onClick={() => setWorkflowDocViewMode("cards")}
                >
                  Cards only
                </button>
              </div>

              {workflowDocViewMode === "table" ? (
                <>
                  <div className="training-workflow-docs-actions">
                    <p>
                      {selectedWorkflowDoc
                        ? `Selected: ${selectedWorkflowDoc.title}`
                        : "Select a document from the table to view it."}
                    </p>
                    <button
                      type="button"
                      className="home-btn home-btn--solid"
                      disabled={!selectedWorkflowDocUrl}
                      onClick={() => setShowWorkflowDocModal(true)}
                    >
                      View Selected Document
                    </button>
                  </div>

                  <div className="training-workflow-docs-grid" aria-label="Workflow document library">
                    <AgGridReact
                      theme={agGridTheme}
                      rowData={workflowDocuments}
                      columnDefs={workflowDocColumnDefs}
                      defaultColDef={workflowDocDefaultColDef}
                      rowSelection={{
                        mode: "singleRow",
                        enableClickSelection: "enableSelection",
                      }}
                      onRowClicked={(event) => setSelectedWorkflowDoc(event.data)}
                      onSelectionChanged={(event) => {
                        const selectedRows = event.api.getSelectedRows();
                        setSelectedWorkflowDoc(selectedRows?.[0] ?? null);
                      }}
                      onGridReady={(event) => updateWorkflowDocCount(event.api)}
                      onModelUpdated={(event) => updateWorkflowDocCount(event.api)}
                      onRowDoubleClicked={(event) => openWorkflowDocument(event.data)}
                      gridOptions={{
                        animateRows: true,
                        pagination: false,
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="training-workflow-cards-filter-row">
                    <div className="training-workflow-cards-filter training-workflow-cards-filter--search">
                      <label
                        className="training-workflow-cards-filter__label"
                        htmlFor="workflow-card-search"
                      >
                        Search Documents
                      </label>
                      <input
                        id="workflow-card-search"
                        type="text"
                        placeholder="Type to search by title..."
                        value={workflowDocCardSearchTerm}
                        onChange={(event) =>
                          setWorkflowDocCardSearchTerm(event.target.value)
                        }
                        className="training-workflow-cards-filter__input"
                      />
                    </div>

                    <div className="training-workflow-cards-filter">
                      <label
                        className="training-workflow-cards-filter__label"
                        htmlFor="workflow-card-region-filter"
                      >
                        Region
                      </label>
                      <select
                        id="workflow-card-region-filter"
                        className="training-workflow-cards-filter__select"
                        value={workflowDocCardRegionFilter}
                        onChange={(event) =>
                          setWorkflowDocCardRegionFilter(event.target.value)
                        }
                      >
                        {workflowDocCardRegions.map((region) => (
                          <option key={region} value={region}>
                            {region}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="training-workflow-cards-filter">
                      <label
                        className="training-workflow-cards-filter__label"
                        htmlFor="workflow-card-category-filter"
                      >
                        Category
                      </label>
                      <select
                        id="workflow-card-category-filter"
                        className="training-workflow-cards-filter__select"
                        value={workflowDocCardCategoryFilter}
                        onChange={(event) =>
                          setWorkflowDocCardCategoryFilter(event.target.value)
                        }
                      >
                        {workflowDocCardCategories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {filteredWorkflowDocCards.length > 0 ? (
                    <div className="training-workflow-tile-grid" aria-label="Workflow document tiles">
                      {filteredWorkflowDocCards.map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          className={`training-workflow-tile ${selectedWorkflowDoc?.id === doc.id ? "training-workflow-tile--selected" : ""}`}
                          onClick={() => openWorkflowDocument(doc)}
                        >
                          <p className="training-workflow-tile__meta">{doc.region} | {doc.category}</p>
                          <h4>{doc.title}</h4>
                          <span>Open document</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="training-empty-state">
                      No workflow documents found yet.
                    </div>
                  )}
                </>
              )}

              <Modal
                show={showWorkflowDocModal}
                onHide={() => setShowWorkflowDocModal(false)}
                fullscreen
                dialogClassName="training-workflow-docs-modal project-config-modal"
              >
                <Modal.Header className="project-config-modal__header">
                  <Modal.Title>
                    {selectedWorkflowDoc?.title ?? "Workflow Document"}
                  </Modal.Title>
                  <div
                    data-bs-theme="dark"
                    style={{
                      position: "absolute",
                      right: 20,
                    }}
                  >
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowWorkflowDocModal(false)}
                      aria-label="Close"
                    />
                  </div>
                </Modal.Header>
                <Modal.Body className="project-config-modal__body training-workflow-docs-modal__body">
                  {selectedWorkflowDocPreviewUrl ? (
                    <iframe
                      title={selectedWorkflowDoc?.title ?? "Workflow Document"}
                      src={selectedWorkflowDocPreviewUrl}
                      className="training-workflow-docs-modal__frame"
                    />
                  ) : (
                    <div className="training-workflow-docs-modal__fallback">
                      <h3>Preview not available for this file type</h3>
                      <p>
                        This file cannot be previewed directly in the modal.
                        Use the actions below to open or download it.
                      </p>
                      <div className="training-workflow-docs-modal__fallback-actions">
                        {selectedWorkflowDocUrl && (
                          <a
                            href={selectedWorkflowDocUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="home-btn home-btn--solid"
                          >
                            Open Document
                          </a>
                        )}
                        {selectedWorkflowDocUrl && (
                          <a
                            href={selectedWorkflowDocUrl}
                            download
                            className="home-btn home-btn--ghost"
                          >
                            Download Document
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </Modal.Body>
              </Modal>
            </section>

            <section id="training-super-users" className="home-section training-hub-section">
              <div className="home-section__header">
                <p className="home-kicker">KEY CONTACTS</p>
                <h2>Key contacts by region</h2>
              </div>

              <div className="training-super-users__intro-row">
                <p className="training-super-users__intro">
                  Contact your regional key contact first for guidance and
                  troubleshooting. If the issue needs formal tracking, raise a
                  support ticket.
                </p>

                <a
                  href={TICKET_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-btn home-btn--solid"
                >
                  Raise a Ticket
                </a>
              </div>

              <div className="training-super-users-grid" aria-label="Super users by region">
                <AgGridReact
                  theme={agGridTheme}
                  rowData={SUPER_USERS}
                  columnDefs={superUserColumnDefs}
                  defaultColDef={superUserDefaultColDef}
                  gridOptions={{
                    animateRows: true,
                    pagination: false,
                  }}
                />
              </div>

            </section>

            <section id="training-feedback" className="home-section training-hub-section">
              <div className="home-section__header">
                <p className="home-kicker">FEEDBACK</p>
                <h2>
                  {internalSelected === "NA"
                    ? "Choose a form type to continue"
                    : internalSelected === "yes"
                      ? "Internal Team Feedback"
                      : "External Partner Feedback"}
                </h2>
              </div>

              <div
                className="feedback-selector"
                role="group"
                aria-label="Feedback form selection"
              >
                <button
                  type="button"
                  className={`home-btn ${
                    internalSelected === "yes"
                      ? "home-btn--solid feedback-btn--active"
                      : "home-btn--ghost"
                  }`}
                  onClick={() => setInternalSelected("yes")}
                >
                  MWH Treatment Employee
                </button>
                <button
                  type="button"
                  className={`home-btn ${
                    internalSelected === "no"
                      ? "home-btn--solid feedback-btn--active"
                      : "home-btn--ghost"
                  }`}
                  onClick={() => setInternalSelected("no")}
                >
                  External User
                </button>
              </div>

              {feedbackSrc ? (
                <div className="feedback-form-frame-wrap">
                  <iframe
                    src={feedbackSrc}
                    title={
                      internalSelected === "yes"
                        ? "Internal Feedback Form"
                        : "External Feedback Form"
                    }
                    className="feedback-form-frame"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="feedback-empty-state">
                  Select whether you are an internal or external user to load
                  the correct feedback form.
                </div>
              )}
            </section>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Help;
